/**
 * Performance monitoring utilities for Core Web Vitals
 * Tracks LCP, INP, CLS and other performance metrics
 */

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

interface CoreWebVitalsThresholds {
  LCP: { good: number; poor: number };
  INP: { good: number; poor: number };
  CLS: { good: number; poor: number };
  FID: { good: number; poor: number };
}

// Core Web Vitals thresholds (in milliseconds for timing metrics)
const THRESHOLDS: CoreWebVitalsThresholds = {
  LCP: { good: 2500, poor: 4000 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 }
};

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observer: PerformanceObserver | null = null;

  constructor() {
    this.initializeObserver();
    this.trackNavigationTiming();
  }

  private initializeObserver() {
    if (!('PerformanceObserver' in window)) return;

    try {
      this.observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          this.handlePerformanceEntry(entry);
        });
      });

      // Observe Core Web Vitals
      this.observer.observe({ 
        type: 'largest-contentful-paint', 
        buffered: true 
      });
      
      this.observer.observe({ 
        type: 'first-input', 
        buffered: true 
      });
      
      this.observer.observe({ 
        type: 'layout-shift', 
        buffered: true 
      });

      // Observe navigation and resource timing
      this.observer.observe({ 
        type: 'navigation', 
        buffered: true 
      });
      
      this.observer.observe({ 
        type: 'resource', 
        buffered: true 
      });

    } catch (error) {
      console.warn('Performance observer not supported:', error);
    }
  }

  private handlePerformanceEntry(entry: PerformanceEntry) {
    switch (entry.entryType) {
      case 'largest-contentful-paint':
        this.recordMetric('LCP', entry.startTime);
        break;
      
      case 'first-input':
        const fidEntry = entry as PerformanceEventTiming;
        this.recordMetric('FID', fidEntry.processingStart - fidEntry.startTime);
        break;
      
      case 'layout-shift':
        const clsEntry = entry as any; // LayoutShift type not fully supported
        if (!clsEntry.hadRecentInput) {
          this.recordMetric('CLS', clsEntry.value);
        }
        break;
      
      case 'navigation':
        this.handleNavigationTiming(entry as PerformanceNavigationTiming);
        break;
      
      case 'resource':
        this.handleResourceTiming(entry as PerformanceResourceTiming);
        break;
    }
  }

  private handleNavigationTiming(entry: PerformanceNavigationTiming) {
    const timing = {
      'DNS Lookup': entry.domainLookupEnd - entry.domainLookupStart,
      'TCP Connection': entry.connectEnd - entry.connectStart,
      'TLS Handshake': entry.secureConnectionStart > 0 ? 
        entry.connectEnd - entry.secureConnectionStart : 0,
      'First Byte (TTFB)': entry.responseStart - entry.requestStart,
      'DOM Content Loaded': entry.domContentLoadedEventEnd - entry.startTime,
      'Load Complete': entry.loadEventEnd - entry.startTime
    };

    Object.entries(timing).forEach(([name, value]) => {
      if (value > 0) {
        this.recordMetric(name, value);
      }
    });
  }

  private handleResourceTiming(entry: PerformanceResourceTiming) {
    // Track slow resources (>1s)
    const duration = entry.responseEnd - entry.startTime;
    if (duration > 1000) {
      console.warn(`Slow resource detected: ${entry.name} (${duration.toFixed(2)}ms)`);
    }

    // Track large resources
    const transferSize = (entry as any).transferSize || 0;
    if (transferSize > 1024 * 1024) { // >1MB
      console.warn(`Large resource detected: ${entry.name} (${(transferSize / 1024 / 1024).toFixed(2)}MB)`);
    }
  }

  private recordMetric(name: string, value: number) {
    const rating = this.getRating(name, value);
    const metric: PerformanceMetric = {
      name,
      value,
      rating,
      timestamp: Date.now()
    };

    this.metrics.push(metric);
    this.reportMetric(metric);
  }

  private getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const threshold = THRESHOLDS[name as keyof CoreWebVitalsThresholds];
    if (!threshold) return 'good';
    
    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }

  private reportMetric(metric: PerformanceMetric) {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      const color = metric.rating === 'good' ? 'green' : 
                   metric.rating === 'needs-improvement' ? 'orange' : 'red';
      console.log(
        `%c${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`,
        `color: ${color}; font-weight: bold;`
      );
    }

    // Send to analytics in production
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (window as any).gtag;
      gtag('event', 'core_web_vital', {
        event_category: 'Performance',
        event_label: metric.name,
        value: Math.round(metric.value),
        custom_map: { rating: metric.rating }
      });
    }
  }

  public getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  public getMetricsByType(type: string): PerformanceMetric[] {
    return this.metrics.filter(m => m.name === type);
  }

  public getWorstMetrics(): PerformanceMetric[] {
    return this.metrics.filter(m => m.rating === 'poor');
  }

  private trackNavigationTiming() {
    // Track page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          this.handleNavigationTiming(navigation);
        }
      }, 0);
    });
  }

  public disconnect() {
    this.observer?.disconnect();
  }
}

// Singleton instance
let performanceMonitor: PerformanceMonitor | null = null;

export const initializePerformanceMonitoring = () => {
  if (!performanceMonitor && typeof window !== 'undefined') {
    performanceMonitor = new PerformanceMonitor();
  }
  return performanceMonitor;
};

export const getPerformanceMetrics = () => {
  return performanceMonitor?.getMetrics() || [];
};

export const getWorstPerformanceMetrics = () => {
  return performanceMonitor?.getWorstMetrics() || [];
};

// Utility function to measure custom timing
export const measurePerformance = (name: string, fn: () => void | Promise<void>) => {
  const start = performance.now();
  const result = fn();
  
  if (result instanceof Promise) {
    return result.finally(() => {
      const duration = performance.now() - start;
      console.log(`${name}: ${duration.toFixed(2)}ms`);
    });
  } else {
    const duration = performance.now() - start;
    console.log(`${name}: ${duration.toFixed(2)}ms`);
    return result;
  }
};

// Image loading performance tracker
export const trackImagePerformance = (src: string, startTime: number) => {
  const loadTime = performance.now() - startTime;
  
  if (loadTime > 1000) { // Log slow images
    console.warn(`Slow image load: ${src} (${loadTime.toFixed(2)}ms)`);
  }

  // Report to analytics
  if (typeof window !== 'undefined' && 'gtag' in window) {
    const gtag = (window as any).gtag;
    gtag('event', 'image_load_time', {
      event_category: 'Performance',
      event_label: src,
      value: Math.round(loadTime)
    });
  }
};

// Bundle size monitoring
export const trackBundleSize = () => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection) {
      console.log('Network Info:', {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      });
    }
  }
};

// Critical Resource Performance
export const preloadCriticalResources = () => {
  const criticalResources = [
    '/src/index.css',
    '/src/main.tsx'
  ];
  
  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    link.as = resource.endsWith('.css') ? 'style' : 'script';
    document.head.appendChild(link);
  });
};